/**
 * Operator — one-per-server controls: YouTube cookies, residential proxy,
 * source cache, the A/V-sync guard, and channel features. Not part of a
 * brand's own Settings; relocated out of Settings.tsx so the accounts
 * sub-project can later gate this behind an operator role instead of the
 * single-login `QL_OPERATOR_PANEL` flag. Every card below is byte-stable
 * from its prior home in Settings.tsx — this is a relocation, not a rewrite.
 */
import { AlertTriangle, Check, Trash2 } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  clearCookies as apiClearCookies,
  clearProxy as apiClearProxy,
  clearWebshareKey as apiClearWebshare,
  evictCache,
  getAvSyncGuardStatus,
  getCacheStats,
  getCookiesStatus,
  getProxyStatus,
  getWebshareStatus,
  listChannelsApi,
  saveAvSyncGuardMode as apiSaveAvSyncGuardMode,
  saveCookies as apiSaveCookies,
  saveProxy as apiSaveProxy,
  saveWebshareKey as apiSaveWebshare,
  updateChannelApi,
  type AvSyncGuardStatus,
  type AvSyncMode,
  type CacheStats,
  type CookiesStatus,
  type ProxyStatus,
  type WebshareStatus,
} from "../api";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { PageHeader } from "../components/PageHeader";
import {
  KV,
  LoadingCard,
  PillStatus,
  SettingCard,
  formatBytes,
  formatRelativeTime,
} from "../components/settings/primitives";
import { cardStyle, ctaSmallStyle } from "../components/styles";
import { useIsMobile } from "../lib/useIsMobile";

export function Operator({ channelsEnabled }: { channelsEnabled: boolean }): React.JSX.Element {
  const [cache, setCache] = useState<CacheStats | null>(null);
  const [evicting, setEvicting] = useState(false);
  const [cookies, setCookies] = useState<CookiesStatus | null>(null);

  const refreshCache = useCallback((): void => {
    getCacheStats()
      .then(setCache)
      .catch(() => {
        /* non-fatal */
      });
  }, []);

  const refreshCookies = useCallback((): void => {
    getCookiesStatus()
      .then(setCookies)
      .catch(() => {
        /* non-fatal */
      });
  }, []);

  useEffect(() => {
    refreshCache();
    refreshCookies();
  }, [refreshCache, refreshCookies]);

  const handleEvict = async (): Promise<void> => {
    setEvicting(true);
    try {
      await evictCache();
      refreshCache();
    } finally {
      setEvicting(false);
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Operator"
        title="Server controls."
        subtitle="One per server, managed by us. Not part of a brand."
      />
      <PoolCard />
      <ProxyCard />
      {cookies ? (
        <CookiesCard status={cookies} onSaved={(s) => setCookies(s)} />
      ) : (
        <LoadingCard />
      )}
      {cache ? (
        <CacheSection cache={cache} evicting={evicting} onEvict={() => void handleEvict()} />
      ) : (
        <LoadingCard />
      )}
      <AvSyncGuardCard />
      {channelsEnabled && <ChannelFeaturesSection />}
    </div>
  );
}

// ---------- SOURCE CACHE --------------------------------------------------

function CacheSection({
  cache,
  evicting,
  onEvict,
}: {
  cache: CacheStats;
  evicting: boolean;
  onEvict: () => void;
}): React.JSX.Element {
  const isMobile = useIsMobile();
  return (
    <SettingCard
      title="Source cache"
      sub="yt-dlp downloads land here so re-rendering a different segment of the same video doesn't re-download. Evicted by age and size cap."
    >
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 16 }}>
        <CacheStat label="Entries" value={String(cache.entries)} />
        <CacheStat label="On disk" value={formatBytes(cache.bytes)} />
        <CacheStat label="Cap" value={formatBytes(cache.config.maxBytes)} />
        <CacheStat label="TTL" value={`${cache.config.maxAgeDays} days`} />
      </div>
      <div
        style={{
          padding: 12,
          borderRadius: 10,
          background: "rgba(255,255,255,.02)",
          border: "1px solid rgba(255,255,255,.05)",
          fontSize: 11,
          color: "var(--ql-fg-muted)",
          fontFamily: "var(--ql-font-mono)",
          marginBottom: 16,
          wordBreak: "break-all",
        }}
      >
        {cache.config.dir}
      </div>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={onEvict}
          disabled={evicting || cache.entries === 0}
          style={{
            ...ctaSmallStyle,
            background: "rgba(255,255,255,.04)",
            color: "var(--ql-gray-200)",
            border: "1px solid rgba(255,255,255,.10)",
            boxShadow: "none",
            cursor: evicting || cache.entries === 0 ? "not-allowed" : "pointer",
            opacity: evicting || cache.entries === 0 ? 0.5 : 1,
          }}
        >
          <Trash2 size={13} strokeWidth={2} />
          {evicting ? "Evicting…" : "Run eviction"}
        </button>
        <span style={{ fontSize: 11, color: "var(--ql-fg-muted)" }}>
          Drops entries older than the TTL or above the size cap. Set{" "}
          <code style={{ fontFamily: "var(--ql-font-mono)" }}>QL_CACHE_*</code> env vars to tune.
        </span>
      </div>
    </SettingCard>
  );
}

function CacheStat({ label, value }: { label: string; value: string }): React.JSX.Element {
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        background: "rgba(255,255,255,.02)",
        border: "1px solid rgba(255,255,255,.06)",
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: "var(--ql-fg-muted)",
          textTransform: "uppercase",
          letterSpacing: ".06em",
          fontWeight: 700,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 20,
          fontWeight: 800,
          fontFamily: "var(--ql-font-mono)",
          color: "var(--ql-yellow)",
          letterSpacing: "-0.02em",
        }}
      >
        {value}
      </div>
    </div>
  );
}

// ---------- A/V SYNC GUARD -------------------------------------------------

// AvSyncGuardCard — flip the audio/video sync guard between Off / Warn / Block.
// The guard samples frames after the crop and verifies the on-screen person
// still matches the speaker; "Block" stops any clip whose picture has drifted.
// Saved to data/av-sync-guard.json and applied on the next render — no restart.
const AV_SYNC_OPTIONS: Array<{
  mode: AvSyncMode;
  label: string;
  blurb: string;
  recommended?: boolean;
}> = [
  { mode: "off", label: "Off", blurb: "Don't check. Videos are made exactly as before." },
  {
    mode: "warn",
    label: "Warn",
    blurb:
      "Check every video and flag it in the Library when the person on screen doesn't match who's talking — but still deliver it.",
  },
  {
    mode: "block",
    label: "Block",
    blurb:
      "Check every video and stop it from going out when the person on screen doesn't match who's talking.",
    recommended: true,
  },
];

function AvSyncGuardCard(): React.JSX.Element {
  const [status, setStatus] = useState<AvSyncGuardStatus | null>(null);
  const [busy, setBusy] = useState<AvSyncMode | null>(null);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getAvSyncGuardStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSelect = async (mode: AvSyncMode): Promise<void> => {
    if (status?.mode === mode) return;
    setBusy(mode);
    setMsg(null);
    try {
      const next = await apiSaveAvSyncGuardMode(mode);
      setStatus(next);
      setMsg({
        kind: "ok",
        text:
          mode === "off"
            ? "Guard off — renders run unchanged."
            : mode === "warn"
            ? "Guard set to Warn — drift will be flagged but videos still ship."
            : "Guard set to Block — a clip that drifts will be stopped before it ships.",
      });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(null);
    }
  };

  const active = status?.mode ?? "off";

  return (
    <SettingCard
      title="Picture-matches-audio guard"
      sub={
        <>
          After each video is made, double-check that the person on screen is the one actually
          talking — catching the "wrong person on screen / picture drifted off the audio" problem that
          nothing else checks. <strong style={{ color: "var(--ql-gray-200)" }}>Block</strong> is the
          setting that actually stops a bad video from going out.
        </>
      }
    >
      {msg && (
        <div
          role={msg.kind === "err" ? "alert" : "status"}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            padding: "10px 12px",
            marginBottom: 14,
            background: msg.kind === "err" ? "rgba(248,113,113,.10)" : "rgba(74,222,128,.10)",
            border: `1px solid ${msg.kind === "err" ? "rgba(248,113,113,.35)" : "rgba(74,222,128,.30)"}`,
            borderRadius: 8,
            color: msg.kind === "err" ? "#FCA5A5" : "#86EFAC",
            fontSize: 12,
          }}
        >
          {msg.kind === "err" ? (
            <AlertTriangle size={14} strokeWidth={2} />
          ) : (
            <Check size={14} strokeWidth={2.5} />
          )}
          <span>{msg.text}</span>
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {AV_SYNC_OPTIONS.map((opt) => {
          const selected = active === opt.mode;
          const isBusy = busy === opt.mode;
          return (
            <button
              key={opt.mode}
              type="button"
              onClick={() => void handleSelect(opt.mode)}
              disabled={busy !== null}
              style={{
                display: "flex",
                alignItems: "flex-start",
                gap: 12,
                textAlign: "left",
                padding: "12px 14px",
                borderRadius: 12,
                background: selected ? "rgba(255,213,0,.08)" : "rgba(255,255,255,.02)",
                border: selected
                  ? "1px solid rgba(255,213,0,.35)"
                  : "1px solid rgba(255,255,255,.08)",
                color: "var(--ql-fg)",
                cursor: busy !== null ? "not-allowed" : "pointer",
                opacity: busy !== null && !isBusy ? 0.6 : 1,
                fontFamily: "inherit",
              }}
            >
              <span
                aria-hidden
                style={{
                  marginTop: 2,
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  borderRadius: 999,
                  border: selected
                    ? "5px solid var(--ql-yellow)"
                    : "2px solid rgba(255,255,255,.25)",
                  boxSizing: "border-box",
                }}
              />
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 700,
                      color: selected ? "var(--ql-yellow)" : "var(--ql-fg)",
                    }}
                  >
                    {opt.label}
                    {isBusy ? " · saving…" : ""}
                  </span>
                  {opt.recommended && (
                    <span
                      style={{
                        fontSize: 9,
                        fontWeight: 800,
                        letterSpacing: ".06em",
                        textTransform: "uppercase",
                        padding: "2px 6px",
                        borderRadius: 999,
                        color: "var(--ql-yellow)",
                        background: "rgba(255,213,0,.12)",
                        border: "1px solid rgba(255,213,0,.30)",
                      }}
                    >
                      Recommended
                    </span>
                  )}
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 12,
                    color: "var(--ql-fg-muted)",
                    marginTop: 3,
                    lineHeight: 1.5,
                  }}
                >
                  {opt.blurb}
                </span>
              </span>
            </button>
          );
        })}
      </div>

      {status?.source === "env" && (
        <div
          style={{
            marginTop: 12,
            padding: "8px 12px",
            borderRadius: 8,
            fontSize: 11,
            lineHeight: 1.5,
            color: "#FBBF24",
            background: "rgba(251,191,36,.08)",
            border: "1px solid rgba(251,191,36,.22)",
          }}
        >
          Currently set via the{" "}
          <code style={{ fontFamily: "var(--ql-font-mono)" }}>QL_AV_SYNC_GUARD</code> env var. Picking
          a mode here overrides it.
        </div>
      )}
    </SettingCard>
  );
}

// ===========================================================================
// PoolCard — Webshare API key → auto-failover proxy pool. Paste the key once;
// the editor pulls the live IP list and rotates automatically when YouTube
// flags one. Key stored chmod 600 in data/, never sent back to the client.
// ===========================================================================

function PoolCard(): React.JSX.Element {
  const [status, setStatus] = useState<WebshareStatus | null>(null);
  const [key, setKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getWebshareStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (): Promise<void> => {
    if (!key.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      const next = await apiSaveWebshare(key.trim());
      setStatus(next);
      setKey("");
      setMsg({ kind: "ok", text: `Connected · ${next.pool.total} proxies in the pool.` });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const confirmClear = async (): Promise<void> => {
    setConfirmClearOpen(false);
    setBusy(true);
    setMsg(null);
    try {
      const next = await apiClearWebshare();
      setStatus(next);
      setMsg({ kind: "ok", text: "Pool disconnected." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const configured = status?.configured ?? false;
  const fromSettings = status?.source === "settings";
  const pool = status?.pool;

  return (
    <>
      <div style={{ ...cardStyle, padding: 24, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Residential proxy pool · auto-failover</div>
        <div
          style={{
            fontSize: 12,
            color: "var(--ql-fg-muted)",
            marginBottom: 16,
            lineHeight: 1.55,
          }}
        >
          Paste your <strong style={{ color: "var(--ql-gray-200)" }}>Webshare API key</strong> and the
          editor pulls your whole IP list and rotates to the next one automatically whenever YouTube
          flags an IP — no manual swapping. It remembers which IPs are flagged and self-heals. Get the
          key at{" "}
          <a href="https://dashboard.webshare.io/userapi/keys" target="_blank" rel="noreferrer" style={{ color: "var(--ql-yellow)" }}>
            dashboard.webshare.io → API → Keys
          </a>
          . Stored in <code style={{ fontFamily: "var(--ql-font-mono)" }}>data/webshare.json</code> (chmod 600).
        </div>

        {msg && (
          <div
            role={msg.kind === "err" ? "alert" : "status"}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "10px 12px",
              marginBottom: 14,
              background: msg.kind === "err" ? "rgba(248,113,113,.10)" : "rgba(74,222,128,.10)",
              border: `1px solid ${msg.kind === "err" ? "rgba(248,113,113,.35)" : "rgba(74,222,128,.30)"}`,
              borderRadius: 8,
              color: msg.kind === "err" ? "#FCA5A5" : "#86EFAC",
              fontSize: 12,
            }}
          >
            {msg.kind === "err" ? <AlertTriangle size={14} strokeWidth={2} /> : <Check size={14} strokeWidth={2.5} />}
            <span>{msg.text}</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <PillStatus
            ok={configured}
            label={
              configured && pool
                ? `Active · ${pool.total} proxies${pool.healthy < pool.valid ? ` (${pool.healthy} healthy)` : ""}`
                : "Active"
            }
            badLabel="Not set"
          />
          {status?.source === "env" && <KV label="Source" value="env · WEBSHARE_API_KEY" />}
          {fromSettings && status?.savedAt && <KV label="Saved" value={formatRelativeTime(status.savedAt)} />}
        </div>

        <input
          type="password"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          placeholder="Webshare API key"
          autoComplete="off"
          spellCheck={false}
          style={{
            width: "100%",
            fontFamily: "var(--ql-font-mono)",
            fontSize: 12,
            padding: "10px 12px",
            background: "rgba(0,0,0,.30)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: 8,
            color: "var(--ql-fg)",
            outline: "none",
            marginBottom: 12,
            boxSizing: "border-box",
          }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !key.trim()}
            style={{ ...ctaSmallStyle, cursor: busy || !key.trim() ? "not-allowed" : "pointer", opacity: busy || !key.trim() ? 0.5 : 1 }}
          >
            {busy ? "Validating…" : "Connect pool"}
          </button>
          {fromSettings && (
            <button
              type="button"
              onClick={() => setConfirmClearOpen(true)}
              disabled={busy}
              style={{
                ...ctaSmallStyle,
                background: "rgba(255,255,255,.04)",
                color: "var(--ql-gray-200)",
                border: "1px solid rgba(255,255,255,.10)",
                boxShadow: "none",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
              Disconnect
            </button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmClearOpen}
        title="Disconnect the proxy pool?"
        message="yt-dlp will fall back to the single proxy below (or none). You can reconnect with your API key any time."
        confirmLabel="Disconnect"
        danger
        busy={busy}
        onConfirm={() => void confirmClear()}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </>
  );
}

// ===========================================================================
// ProxyCard — residential proxy URL for yt-dlp egress. Self-contained, mirrors
// the cookies/Buffer secret-management pattern: paste → save to data/ (chmod
// 600). The status is masked (scheme/host/port only) — the URL with its
// embedded credentials is never sent back to the client.
// ===========================================================================

function ProxyCard(): React.JSX.Element {
  const [status, setStatus] = useState<ProxyStatus | null>(null);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getProxyStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch(() => {
        /* non-fatal */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSave = async (): Promise<void> => {
    if (!url.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      const next = await apiSaveProxy(url.trim());
      setStatus(next);
      setUrl("");
      setMsg({
        kind: "ok",
        text: `Saved · egressing through ${next.scheme}://${next.host}:${next.port}.`,
      });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const confirmClear = async (): Promise<void> => {
    setConfirmClearOpen(false);
    setBusy(true);
    setMsg(null);
    try {
      const next = await apiClearProxy();
      setStatus(next);
      setMsg({ kind: "ok", text: "Proxy cleared." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const configured = status?.configured ?? false;
  const fromEnv = status?.source === "env";
  const fromSettings = status?.source === "settings";

  return (
    <>
      <div style={{ ...cardStyle, padding: 24, marginBottom: 14 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>Residential proxy</div>
        <div
          style={{
            fontSize: 12,
            color: "var(--ql-fg-muted)",
            marginBottom: 16,
            lineHeight: 1.55,
          }}
        >
          YouTube flags the server's datacenter IP and trips the "confirm you're not a bot" wall —
          the real reason cookies keep needing a refresh. Point yt-dlp at a{" "}
          <strong style={{ color: "var(--ql-gray-200)" }}>static residential proxy</strong> and the
          wall stops firing; public videos then download without the cookie churn. Stored in{" "}
          <code style={{ fontFamily: "var(--ql-font-mono)" }}>data/ytdlp-proxy.json</code> (chmod 600);
          the credentials are never shown again.
        </div>

        {msg && (
          <div
            role={msg.kind === "err" ? "alert" : "status"}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "10px 12px",
              marginBottom: 14,
              background: msg.kind === "err" ? "rgba(248,113,113,.10)" : "rgba(74,222,128,.10)",
              border: `1px solid ${msg.kind === "err" ? "rgba(248,113,113,.35)" : "rgba(74,222,128,.30)"}`,
              borderRadius: 8,
              color: msg.kind === "err" ? "#FCA5A5" : "#86EFAC",
              fontSize: 12,
            }}
          >
            {msg.kind === "err" ? (
              <AlertTriangle size={14} strokeWidth={2} />
            ) : (
              <Check size={14} strokeWidth={2.5} />
            )}
            <span>{msg.text}</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
          <PillStatus
            ok={configured}
            label={
              configured && status?.host
                ? `Active · ${status.scheme}://${status.host}:${status.port}`
                : "Active"
            }
            badLabel="Not set"
          />
          {fromEnv && <KV label="Source" value="env · YTDLP_PROXY" />}
          {status?.hasAuth && <KV label="Auth" value="credentials embedded" />}
          {fromSettings && status?.updatedAt && (
            <KV label="Saved" value={formatRelativeTime(status.updatedAt)} />
          )}
        </div>

        {fromEnv && (
          <div
            style={{
              marginBottom: 12,
              padding: "8px 12px",
              borderRadius: 8,
              fontSize: 11,
              lineHeight: 1.5,
              color: "#FBBF24",
              background: "rgba(251,191,36,.08)",
              border: "1px solid rgba(251,191,36,.22)",
            }}
          >
            Currently set via the <code style={{ fontFamily: "var(--ql-font-mono)" }}>YTDLP_PROXY</code>{" "}
            env var. Saving here overrides it; the env value stays as the fallback.
          </div>
        )}

        <input
          type="password"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="http://user:pass@host:port"
          autoComplete="off"
          spellCheck={false}
          style={{
            width: "100%",
            fontFamily: "var(--ql-font-mono)",
            fontSize: 12,
            padding: "10px 12px",
            background: "rgba(0,0,0,.30)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: 8,
            color: "var(--ql-fg)",
            outline: "none",
            marginBottom: 12,
            boxSizing: "border-box",
          }}
        />
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !url.trim()}
            style={{
              ...ctaSmallStyle,
              cursor: busy || !url.trim() ? "not-allowed" : "pointer",
              opacity: busy || !url.trim() ? 0.5 : 1,
            }}
          >
            {busy ? "Saving…" : "Save proxy"}
          </button>
          {fromSettings && (
            <button
              type="button"
              onClick={() => setConfirmClearOpen(true)}
              disabled={busy}
              style={{
                ...ctaSmallStyle,
                background: "rgba(255,255,255,.04)",
                color: "var(--ql-gray-200)",
                border: "1px solid rgba(255,255,255,.10)",
                boxShadow: "none",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
              Clear
            </button>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmClearOpen}
        title="Clear residential proxy?"
        message="yt-dlp will go back to egressing through the server's datacenter IP, which YouTube may rate-limit or bot-challenge. You can paste a new proxy any time."
        confirmLabel="Clear proxy"
        danger
        busy={busy}
        onConfirm={() => void confirmClear()}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </>
  );
}

// ===========================================================================
// CookiesCard — full real save/clear flow, byte-stable from the previous
// Settings.tsx. Now slotted into the new sidebar nav as a discrete section.
// ===========================================================================

function CookiesCard({
  status,
  onSaved,
}: {
  status: CookiesStatus;
  onSaved: (s: CookiesStatus) => void;
}): React.JSX.Element {
  const [paste, setPaste] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [confirmClearOpen, setConfirmClearOpen] = useState(false);
  const cardRef = useRef<HTMLDivElement | null>(null);

  const fresh = status.exists && status.cookieLines > 0;
  const hasYouTube = status.domains.some(
    (d) => d.endsWith("youtube.com") || d.endsWith("google.com"),
  );
  // A jar that lost its login cookies (yt-dlp writeback after YouTube
  // invalidated the session) downloads as anonymous traffic — worse than
  // no cookies, and invisible unless we say it: file exists, count looks
  // normal, only the NAMES are wrong.
  const loggedOut = fresh && !status.loggedIn;
  // The name check above passed through BOTH dead-session outages while every
  // download was bot-walled. This is the observed truth: YouTube rejected the
  // jar the last time anything asked it.
  const sessionDead = fresh && status.sessionLive === false;

  // Auto-scroll the card into view when an error appears, so a paste
  // failure on a long Settings page can't go unnoticed below the fold.
  useEffect(() => {
    if (msg?.kind === "err") cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [msg]);

  const handleSave = async (): Promise<void> => {
    if (!paste.trim()) return;
    setBusy(true);
    setMsg(null);
    try {
      const next = await apiSaveCookies(paste);
      onSaved(next);
      setPaste("");
      setMsg({
        kind: "ok",
        text: `Saved · ${next.cookieLines} cookies · ${next.domains.length} domain${next.domains.length === 1 ? "" : "s"}.`,
      });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  const handleClear = (): void => setConfirmClearOpen(true);

  const confirmClear = async (): Promise<void> => {
    setConfirmClearOpen(false);
    setBusy(true);
    setMsg(null);
    try {
      const next = await apiClearCookies();
      onSaved(next);
      setMsg({ kind: "ok", text: "Cookies cleared." });
    } catch (err) {
      setMsg({ kind: "err", text: err instanceof Error ? err.message : String(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div ref={cardRef} style={{ ...cardStyle, padding: 24 }}>
        <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>YouTube cookies</div>
        <div
          style={{
            fontSize: 12,
            color: "var(--ql-fg-muted)",
            marginBottom: 16,
            lineHeight: 1.55,
          }}
        >
          yt-dlp uses these for age-restricted, member-only, and premium videos. Stored in{" "}
          <code style={{ fontFamily: "var(--ql-font-mono)" }}>data/yt-cookies.txt</code> (chmod 600),
          reused across jobs until rotated.
        </div>

        {msg && msg.kind === "err" && (
          <div
            role="alert"
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 10,
              padding: "12px 14px",
              marginBottom: 14,
              background: "rgba(248,113,113,.10)",
              border: "1px solid rgba(248,113,113,.35)",
              borderRadius: 8,
              color: "#FCA5A5",
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            <AlertTriangle size={16} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{msg.text}</span>
          </div>
        )}

        <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 14, flexWrap: "wrap" }}>
          <PillStatus
            ok={fresh && hasYouTube && !loggedOut && !sessionDead}
            label="Active"
            badLabel={
              !status.exists
                ? "Not set"
                : loggedOut
                  ? "Logged-out jar"
                  : sessionDead
                    ? "Rejected by YouTube"
                    : "Empty / invalid"
            }
          />
          {status.exists && (
            <>
              <KV label="Cookies" value={String(status.cookieLines)} />
              <KV
                label="Domains"
                value={
                  status.domains.length === 0
                    ? "—"
                    : status.domains.slice(0, 3).join(", ") +
                      (status.domains.length > 3 ? ` +${status.domains.length - 3}` : "")
                }
              />
              <KV
                label="Updated"
                value={status.updatedAt ? formatRelativeTime(status.updatedAt) : "—"}
              />
            </>
          )}
          {sessionDead && !loggedOut && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: "#FCA5A5",
                padding: "4px 10px",
                background: "rgba(248,113,113,.10)",
                border: "1px solid rgba(248,113,113,.35)",
                borderRadius: 999,
              }}
            >
              <AlertTriangle size={12} strokeWidth={2} />
              YouTube REJECTED these cookies
              {status.sessionCheckedAt ? ` ${formatRelativeTime(status.sessionCheckedAt)}` : ""} — the
              names look right but the session is signed out, so downloads hit the bot wall. Sign in
              to YouTube in a private window, export, close the window, paste here.
            </span>
          )}
          {loggedOut && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: "#FCA5A5",
                padding: "4px 10px",
                background: "rgba(248,113,113,.10)",
                border: "1px solid rgba(248,113,113,.35)",
                borderRadius: 999,
              }}
            >
              <AlertTriangle size={12} strokeWidth={2} />
              Logged-OUT cookies (no LOGIN_INFO/SAPISID) — downloads run anonymous and hit the bot
              wall. Sign in to YouTube in a private window, export, close the window, paste here.
            </span>
          )}
          {fresh && !hasYouTube && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: 11,
                color: "#FBBF24",
                padding: "4px 10px",
                background: "rgba(251,191,36,.10)",
                border: "1px solid rgba(251,191,36,.25)",
                borderRadius: 999,
              }}
            >
              <AlertTriangle size={12} strokeWidth={2} />
              No youtube.com cookies — wrong domain export?
            </span>
          )}
        </div>

        <div
          style={{
            marginBottom: 14,
            padding: 14,
            borderRadius: 12,
            background: "rgba(255,213,0,.04)",
            border: "1px solid rgba(255,213,0,.18)",
          }}
        >
          <div
            style={{
              marginBottom: 10,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: ".06em",
              color: "var(--ql-yellow)",
              textTransform: "uppercase",
            }}
          >
            How to export
          </div>
          <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12, lineHeight: 1.7, color: "var(--ql-gray-300)" }}>
            <li>
              Open{" "}
              <a
                href="https://www.youtube.com"
                target="_blank"
                rel="noreferrer"
                style={{ color: "var(--ql-yellow)" }}
              >
                youtube.com
              </a>{" "}
              in a new tab. Confirm you're <strong>logged in</strong> (avatar visible top-right).
            </li>
            <li>
              <strong>On that YouTube tab</strong>, click your cookies-export extension (e.g.{" "}
              <em>Get cookies.txt LOCALLY</em>). The popup must show{" "}
              <code style={{ fontFamily: "var(--ql-font-mono)" }}>
                Get cookies.txt for https://www.youtube.com/
              </code>{" "}
              — if it shows any other domain, you're on the wrong tab.
            </li>
            <li>
              Choose <strong>Netscape</strong> format → <strong>Export</strong> or{" "}
              <strong>Copy</strong>.
            </li>
            <li>
              Paste below → <strong>Save cookies</strong>. Multi-domain pastes are accepted —
              non-YouTube cookies are filtered out automatically.
            </li>
          </ol>
        </div>

        <textarea
          value={paste}
          onChange={(e) => setPaste(e.target.value)}
          placeholder={"# Netscape HTTP Cookie File\n.youtube.com\tTRUE\t/\tTRUE\t...\t...\n..."}
          spellCheck={false}
          rows={8}
          style={{
            width: "100%",
            fontFamily: "var(--ql-font-mono)",
            fontSize: 11,
            padding: 12,
            background: "rgba(0,0,0,.30)",
            border: "1px solid rgba(255,255,255,.08)",
            borderRadius: 8,
            color: "var(--ql-fg)",
            resize: "vertical",
            outline: "none",
            lineHeight: 1.55,
          }}
        />

        <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, flexWrap: "wrap" }}>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy || !paste.trim()}
            style={{
              ...ctaSmallStyle,
              cursor: busy || !paste.trim() ? "not-allowed" : "pointer",
              opacity: busy || !paste.trim() ? 0.5 : 1,
            }}
          >
            {busy ? "Saving…" : "Save cookies"}
          </button>
          {status.exists && status.bytes > 0 && (
            <button
              type="button"
              onClick={handleClear}
              disabled={busy}
              style={{
                ...ctaSmallStyle,
                background: "rgba(255,255,255,.04)",
                color: "var(--ql-gray-200)",
                border: "1px solid rgba(255,255,255,.10)",
                boxShadow: "none",
                cursor: busy ? "not-allowed" : "pointer",
                opacity: busy ? 0.5 : 1,
              }}
            >
              <Trash2 size={13} strokeWidth={2} />
              Clear
            </button>
          )}
          {msg && msg.kind === "ok" && (
            <span style={{ fontSize: 11, color: "#4ADE80", marginLeft: 4, maxWidth: 540 }}>
              {msg.text}
            </span>
          )}
        </div>
      </div>
      <ConfirmDialog
        open={confirmClearOpen}
        title="Clear cookies?"
        message="yt-dlp will fall back to the no-auth path on the next clip. You can paste fresh cookies any time."
        confirmLabel="Clear cookies"
        danger
        busy={busy}
        onConfirm={() => void confirmClear()}
        onCancel={() => setConfirmClearOpen(false)}
      />
    </>
  );
}

// ---------- CHANNEL FEATURES ----------------------------------------------

function ChannelFeaturesSection(): React.JSX.Element {
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [autoScan, setAutoScan] = useState<boolean | null>(null); // null = loading
  const [activeSlug, setActiveSlug] = useState<string | null>(null);

  // Load current autoScan state from the active channel.
  useEffect(() => {
    listChannelsApi()
      .then((r) => {
        const ch = r.channels.find((c) => c.slug === r.activeSlug);
        setActiveSlug(r.activeSlug ?? null);
        setAutoScan(ch?.features.autoScan ?? false);
      })
      .catch(() => {
        setAutoScan(false);
        setActiveSlug(null);
      });
  }, []);

  const handleToggle = async (next: boolean): Promise<void> => {
    if (!activeSlug || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const updated = await updateChannelApi(activeSlug, { features: { autoScan: next } });
      setAutoScan(updated.features.autoScan ?? next);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (autoScan === null) return <LoadingCard />;

  return (
    <SettingCard
      title="Channel features"
      sub="Enable or disable pipeline features for the active channel. Changes take effect immediately."
    >
      <label
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "14px 16px",
          borderRadius: 14,
          background: autoScan ? "rgba(255,213,0,.04)" : "rgba(255,255,255,.02)",
          border: autoScan ? "1px solid rgba(255,213,0,.20)" : "1px solid rgba(255,255,255,.06)",
          cursor: saving ? "wait" : "pointer",
          opacity: saving ? 0.7 : 1,
          transition: "all .15s var(--ql-ease)",
        }}
      >
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ql-fg)" }}>Auto-scan</div>
          <div style={{ fontSize: 12, color: "var(--ql-fg-muted)", marginTop: 2 }}>
            Enables "Auto-scan whole video" in New Job and shows candidate cards in the Backlog.
          </div>
        </div>
        <div
          style={{
            width: 40, height: 22, borderRadius: 999, position: "relative", flexShrink: 0,
            background: autoScan ? "linear-gradient(90deg,#FFD500,#FFA500)" : "rgba(255,255,255,.10)",
            transition: "background .2s var(--ql-ease)",
          }}
        >
          <div
            style={{
              position: "absolute", top: 2, left: autoScan ? 20 : 2, width: 18, height: 18,
              borderRadius: 999, background: "#fff",
              transition: "left .2s var(--ql-ease)",
              boxShadow: "0 2px 4px rgba(0,0,0,.3)",
            }}
          />
        </div>
        <input
          type="checkbox"
          checked={autoScan}
          disabled={saving}
          onChange={(e) => void handleToggle(e.target.checked)}
          style={{ display: "none" }}
        />
      </label>
      {saveError && (
        <div style={{ marginTop: 8, fontSize: 12, color: "var(--ql-danger)" }}>{saveError}</div>
      )}
    </SettingCard>
  );
}
